/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table
} from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit2 } from 'lucide-react'
import Swal from 'sweetalert2'
import api from '../../config/AxiosInstance'
import toast, { Toaster } from 'react-hot-toast'
import Loader from '../../components/atoms/Loader'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '22px',
  fontWeight: '700'
}

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const tableHeaderStyle = {
  fontSize: '13px',
  fontWeight: '700',
  color: '#495057',
  letterSpacing: '0.4px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef',
  whiteSpace: 'nowrap'
}

const labelStyle = {
  fontSize: '0.72rem',
  fontWeight: '700',
  color: '#959FA9',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '0.25rem'
}

const valueStyle = {
  fontSize: '0.95rem',
  fontWeight: '600',
  color: '#333'
}

const highlightCellStyle = {
  backgroundColor: '#fff8f0',
  borderRadius: '6px',
  padding: '4px 8px',
  fontWeight: '700',
  color: 'var(--primary-orange)',
  minWidth: '110px',
  display: 'inline-block',
  textAlign: 'center'
}

function CreateTransferItemIssuePage() {
  const { state } = useLocation()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [items, setItems] = useState([])
  const [warehouseName, setWarehouseName] = useState(
    state?.warehouse_name || ''
  )
  const [seriesOptions, setSeriesOptions] = useState([])
  const [selectedSeries, setSelectedSeries] = useState('MK-SERIES')

  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [editPickedPack, setEditPickedPack] = useState('')
  const [editPickedQty, setEditPickedQty] = useState('')

  const warehouseId = state?.warehouse_id

  useEffect(() => {
    const fetchItems = () => {
      if (!warehouseId || !state?.selectedItems?.length) {
        toast.error('Invalid state. Please go back and try again.')
        navigate(-1)
        return
      }

      const enriched = state.selectedItems.map((item) => {
        const factor =
          parseFloat(item.total_pack) > 0
            ? parseFloat(item.total_qty) / parseFloat(item.total_pack)
            : 1
        return {
          ...item,
          picked_pack: Number(item.total_pack),
          picked_qty: Number(item.total_qty),
          convertion_factor_pack: factor
        }
      })
      setItems(enriched)
    }

    fetchItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetchWarehouseInfo = async () => {
      if (state?.warehouse_name) {
        setWarehouseName(state.warehouse_name)
        return
      }
      if (!warehouseId) return
      try {
        const res = await api.get('/master/warehouses/all', {
          params: { is_parent: 1, forUser: 1 }
        })
        const found = res?.data?.data?.find(
          (w) => w.id === warehouseId || w.warehouse_id === warehouseId
        )
        if (found) {
          setWarehouseName(found.warehouse_name)
        }
      } catch {}
    }

    fetchWarehouseInfo()
  }, [warehouseId, state?.warehouse_name])

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const res = await api.get(
          `master/stocks/show-series?warehouse_id=${warehouseId || ''}`
        )
        const { data } = res.data
        setSeriesOptions(data || [])
        const mkSeries = (data || []).find((s) => s.name === 'MK-SERIES')
        if (mkSeries) {
          setSelectedSeries('MK-SERIES')
        } else if (data && data.length > 0) {
          setSelectedSeries(data[0].name)
        }
      } catch {}
    }
    fetchSeries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId])

  const handleCancel = () => {
    Swal.fire({
      title: 'Cancel Confirmation',
      text: 'Are you sure you want to cancel? All changes will be lost.',
      icon: 'warning',
      iconColor: '#f89d1d',
      showCancelButton: true,
      confirmButtonColor: '#EB5757',
      cancelButtonColor: '#adb5bd',
      confirmButtonText: 'Yes, Cancel',
      cancelButtonText: 'Stay',
      customClass: {
        popup: 'swal-popup-outbound',
        actions: 'swal-cancel-pr'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/item-issue')
      }
    })
  }

  const handleSubmit = async () => {
    if (!selectedSeries) {
      return toast.error('Please select a series')
    }
    try {
      setSubmitting(true)
      const payload = {
        type: state?.targetType || 'karantina',
        series_number: selectedSeries,
        problem: state?.selectedID?.[0]?.problem || '',
        issue_items: items.map((item) => ({
          id: item.issue_id,
          total_pack: Number(item.picked_pack)
        }))
      }
      const res = await api.post('item-issue/transfer', payload)
      if (res.status === 200) {
        toast.success('Ticket created successfully.')
        navigate('/item-issue')
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleOpenEditModal = (item) => {
    setEditingItem(item)
    setEditPickedPack(String(item.picked_pack))
    setEditPickedQty(String(item.picked_qty))
    setShowUpdateModal(true)
  }

  const handleEditPackChange = (val) => {
    setEditPickedPack(val)
    const pack = parseFloat(val) || 0
    const factor = parseFloat(editingItem?.convertion_factor_pack || 1)
    setEditPickedQty(String((pack * factor).toFixed(2)))
  }

  const handleEditQtyChange = (val) => {
    setEditPickedQty(val)
    const qty = parseFloat(val) || 0
    const factor = parseFloat(editingItem?.convertion_factor_pack || 1)
    setEditPickedPack(String(factor > 0 ? (qty / factor).toFixed(4) : 0))
  }

  const handleSaveEdit = () => {
    const pack = parseFloat(editPickedPack) || 0
    const qty = parseFloat(editPickedQty) || 0
    const avail = parseFloat(editingItem?.total_pack || 0)

    if (pack <= 0 || qty <= 0) {
      return toast.error('Picked quantity must be greater than 0')
    }
    if (pack > avail) {
      return toast.error(
        `Picked Qty Kemasan cannot exceed available (${avail})`
      )
    }

    setItems((prev) =>
      prev.map((it) =>
        it.id === editingItem.id
          ? { ...it, picked_pack: pack, picked_qty: qty }
          : it
      )
    )
    setShowUpdateModal(false)
  }

  const fmtQty = (val, uom = '') =>
    `${parseFloat(val || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} ${uom}`.trim()

  const canSubmit = items.length > 0 && selectedSeries && !submitting

  return (
    <Container fluid className="px-4 mb-5">
      <Toaster />
      <Row className="mb-4 align-items-center mt-3">
        <Col className="d-flex align-items-center gap-3">
          <Button
            variant="light"
            className="rounded shadow-sm border p-0 d-flex align-items-center justify-content-center"
            style={{ width: '40px', height: '40px' }}
            onClick={handleCancel}
          >
            <ArrowLeft className="text-secondary" size={20} />
          </Button>
          <div>
            <h3 style={headerTitleStyle} className="mb-0">
              Create Transfer
            </h3>
            <small
              className="text-muted fw-semibold"
              style={{ fontSize: '0.78rem' }}
            >
              Stock Issue › CREATE TRANSFER
            </small>
          </div>
        </Col>
      </Row>

      <Card style={cardStyle}>
        <Row className="g-3 align-items-end">
          <Col md={4}>
            <div style={labelStyle}>Source</div>
            <div
              style={{
                ...valueStyle,
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '10px 14px',
                border: '1px solid #e9ecef',
                fontSize: '0.9rem'
              }}
            >
              {loading ? '...' : warehouseName || '-'}
            </div>
          </Col>
          <Col md={4}>
            <div style={labelStyle}>Type</div>
            <div
              style={{
                ...valueStyle,
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '10px 14px',
                border: '1px solid #e9ecef',
                fontSize: '0.9rem'
              }}
            >
              {(state?.targetType || 'karantina') === 'karantina'
                ? 'Quarantine'
                : state?.targetType === 'reject'
                  ? 'Reject'
                  : 'Restock'}
            </div>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={{ ...labelStyle, marginBottom: '0.4rem' }}>
                Series <sup style={{ color: 'red' }}>*</sup>
              </Form.Label>
              <Form.Select
                value={selectedSeries}
                onChange={(e) => setSelectedSeries(e.target.value)}
                style={{
                  height: '45px',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}
              >
                {seriesOptions.length === 0 ? (
                  <option value="MK-SERIES">MK-SERIES</option>
                ) : (
                  seriesOptions.map((s, i) => (
                    <option key={i} value={s.name}>
                      {s.name}
                    </option>
                  ))
                )}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Card style={cardStyle}>
        <h6
          className="fw-bold text-secondary mb-3"
          style={{
            fontSize: '0.85rem',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}
        >
          Item
        </h6>

        {loading ? (
          <div className="text-center py-5">
            <Loader />
          </div>
        ) : (
          <Table
            hover
            responsive
            className="bg-white"
            style={{
              fontSize: '0.88rem',
              borderRadius: '10px',
              overflow: 'hidden'
            }}
          >
            <thead>
              <tr>
                {[
                  'Item Code – Name',
                  'Batch',
                  'Problem',
                  'Source Slot',
                  'Source Palet',
                  'Available Base UOM Qty',
                  'Available Qty Kemasan',
                  'Picked Base UOM Qty',
                  'Picked Qty Kemasan',
                  'Action'
                ].map((h) => (
                  <th
                    key={h}
                    style={tableHeaderStyle}
                    className="p-2 align-middle"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-muted p-4">
                    No items
                  </td>
                </tr>
              )}
              {items.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td
                    className="p-2 align-middle fw-bold text-dark"
                    style={{ minWidth: '220px' }}
                  >
                    {item.item_code} – {item.item_name}
                  </td>
                  <td className="p-2 align-middle">{item.batch || '-'}</td>
                  <td className="p-2 align-middle">
                    {item.item_problem || '-'}
                  </td>
                  <td className="p-2 align-middle">{item.slot || '-'}</td>
                  <td className="p-2 align-middle">{item.palet_code || '-'}</td>
                  <td className="p-2 align-middle">
                    {fmtQty(
                      Number(item.total_pack) *
                        Number(item.convertion_factor_pack || 1),
                      item.uom_qty
                    )}
                  </td>
                  <td className="p-2 align-middle">
                    {fmtQty(item.total_pack, item.uom_pack)}
                  </td>
                  <td className="p-2 align-middle">
                    <span style={highlightCellStyle}>
                      {fmtQty(item.picked_qty, item.uom_qty)}
                    </span>
                  </td>
                  <td className="p-2 align-middle">
                    <span style={highlightCellStyle}>
                      {fmtQty(item.picked_pack, item.uom_pack)}
                    </span>
                  </td>
                  <td className="p-2 align-middle text-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="d-flex align-items-center justify-content-center p-1"
                      style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '6px'
                      }}
                      onClick={() => handleOpenEditModal(item)}
                      title="Edit Picked Qty"
                    >
                      <Edit2 size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <div className="d-flex justify-content-between align-items-center mt-2 pb-4">
        <Button
          variant="outline-secondary"
          onClick={handleCancel}
          style={{ borderRadius: '8px', fontWeight: '600', minWidth: '100px' }}
        >
          Cancel
        </Button>
        <Button
          variant={canSubmit ? 'warning' : 'secondary'}
          className={canSubmit ? 'text-white fw-bold' : 'opacity-50 fw-bold'}
          disabled={!canSubmit}
          onClick={handleSubmit}
          style={{ borderRadius: '8px', minWidth: '120px' }}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </div>

      <Modal
        show={showUpdateModal}
        onHide={() => setShowUpdateModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title
            style={{ fontSize: '1rem', fontWeight: '700', color: '#333' }}
          >
            Update Item
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {editingItem && (
            <>
              <div className="mb-3 p-3 bg-light rounded-3">
                <div style={labelStyle}>Item Code – Name</div>
                <div style={{ ...valueStyle, fontSize: '0.88rem' }}>
                  {editingItem.item_code} – {editingItem.item_name}
                </div>
              </div>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div style={labelStyle}>Batch</div>
                  <div style={valueStyle}>{editingItem.batch || '-'}</div>
                </Col>
                <Col md={6}>
                  <div style={labelStyle}>Source Slot</div>
                  <div style={valueStyle}>{editingItem.slot || '-'}</div>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div style={labelStyle}>Available Base UOM Qty</div>
                  <div
                    style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      color: '#333'
                    }}
                  >
                    {fmtQty(
                      Number(editingItem.total_pack) *
                        Number(editingItem.convertion_factor_pack || 1),
                      editingItem.uom_qty
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div style={labelStyle}>Available Qty Kemasan</div>
                  <div
                    style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      color: '#333'
                    }}
                  >
                    {fmtQty(editingItem.total_pack, editingItem.uom_pack)}
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      Picked Base UOM Qty{' '}
                      <sup style={{ color: 'var(--primary-orange)' }}>*</sup>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={editPickedQty}
                      onChange={(e) => handleEditQtyChange(e.target.value)}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #f89d1d',
                        fontWeight: '600',
                        background: '#fff8f0'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      Picked Qty Kemasan{' '}
                      <sup style={{ color: 'var(--primary-orange)' }}>*</sup>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.0001"
                      value={editPickedPack}
                      onChange={(e) => handleEditPackChange(e.target.value)}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #f89d1d',
                        fontWeight: '600',
                        background: '#fff8f0'
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                * Changing one field will automatically update the other
              </small>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="outline-secondary"
            onClick={() => setShowUpdateModal(false)}
            style={{ borderRadius: '8px', fontWeight: '600' }}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            className="text-white fw-bold"
            onClick={handleSaveEdit}
            style={{ borderRadius: '8px', minWidth: '80px' }}
          >
            Add
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default CreateTransferItemIssuePage
